import { useState, useMemo, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";
import { useProjectReport } from "@/hooks/useReports";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Users,
  FileSpreadsheet,
  Clock,
  CheckCircle2,
  TrendingUp,
  Calendar,
  ChevronDown,
  ChevronRight,
  User,
  XCircle,
  Timer,
  Wifi,
  WifiOff,
  ShieldAlert,
  RefreshCw,
  ExternalLink,
  Activity,
  Bug,
} from "lucide-react";
import { cn } from "@/lib/utils";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { SyncActivityLog } from "@/components/SyncActivityLog";
import { ProjectActivityLog } from "@/components/ProjectActivityLog";
import { IssueActivityLog } from "@/components/IssueActivityLog";
import { TestCaseActivityLog } from "@/components/TestCaseActivityLog";
import { MappingActivityLog } from "@/components/MappingActivityLog";
import { ReportFilterBar, ReportFilterPanel } from "@/components/ReportFilters";
import { StatusBadge } from "@/components/StatusBadge";
import { IssueDetailDialog } from "@/components/IssueDetailDialog";
import { useRecreateSubscriptions } from "@/hooks/useSync";
import { useReportFilters } from "@/hooks/useReportFilters";
import { toast } from "@/lib/toast";
import { useReportExport } from "@/hooks/useReportExport";
import { normalizeStatus, DONE_STATUSES, type TestCaseStatus } from "@/lib/status-config";
import type { ProjectReportResponse, ReportStep, TesterBreakdownEntry, SourceFile, SyncHealthSummary } from "@/types/api";
import type { IssueSnapshot } from "@/types/domain";
import { scenariosApi } from "@/services/scenarios";

const SOURCE_FILES_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

// --- Due date helpers (mirrors TesterDashboard) ---
const dueDateFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

function parseDueDate(dueDate?: string): Date | null {
  if (!dueDate) return null;
  const parsed = new Date(dueDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDueDate(dueDate: string): string {
  const parsed = parseDueDate(dueDate);
  return parsed ? dueDateFormatter.format(parsed) : dueDate;
}

function isDueDateOverdue(dueDate?: string): boolean {
  const parsed = parseDueDate(dueDate);
  if (!parsed) return false;
  const dueDay = new Date(parsed);
  dueDay.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDay < today;
}

const projectStatusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-info/10 text-info border-info/20' },
  inactive: { label: 'Inactive', color: 'bg-muted text-muted-foreground border-muted' },
  started: { label: 'Started', color: 'bg-success/10 text-success border-success/20' },
  completed: { label: 'Completed', color: 'bg-primary/10 text-primary border-primary/20' },
};

const formatRelativeTime = (isoString?: string) => {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
};

const SyncHealthCard = ({
  syncHealth,
}: {
  syncHealth: SyncHealthSummary;
}) => {
  const hasIssues = syncHealth.expired_subscriptions > 0 || syncHealth.circuit_broken_count > 0;

  return (
    <Card className={hasIssues ? "border-warning/50" : ""}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {hasIssues ? (
            <ShieldAlert className="h-5 w-5 text-warning" />
          ) : (
            <Wifi className="h-5 w-5 text-success" />
          )}
          Sync Health
        </CardTitle>
        <CardDescription>
          {syncHealth.total_subscriptions === 0
            ? "No webhooks configured — sync relies on polling only"
            : `${syncHealth.active_subscriptions} of ${syncHealth.total_subscriptions} webhook${syncHealth.total_subscriptions !== 1 ? 's' : ''} active`}
          {syncHealth.last_synced_at && ` — last sync ${formatRelativeTime(syncHealth.last_synced_at)}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {syncHealth.total_subscriptions === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No webhook subscriptions found. Use "Recreate Subscriptions" below to restore real-time sync.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Summary counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-success/10 text-center">
                <p className="text-xl font-bold text-success">{syncHealth.active_subscriptions}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              <div className={cn("p-3 rounded-lg text-center", syncHealth.expired_subscriptions > 0 ? "bg-warning/10" : "bg-muted/50")}>
                <p className={cn("text-xl font-bold", syncHealth.expired_subscriptions > 0 ? "text-warning" : "text-muted-foreground")}>{syncHealth.expired_subscriptions}</p>
                <p className="text-xs text-muted-foreground">Expired</p>
              </div>
              <div className={cn("p-3 rounded-lg text-center", syncHealth.circuit_broken_count > 0 ? "bg-destructive/10" : "bg-muted/50")}>
                <p className={cn("text-xl font-bold", syncHealth.circuit_broken_count > 0 ? "text-destructive" : "text-muted-foreground")}>{syncHealth.circuit_broken_count}</p>
                <p className="text-xs text-muted-foreground">Circuit Broken</p>
              </div>
              <div className="p-3 rounded-lg bg-info/10 text-center">
                <p className="text-xl font-bold text-info">{formatRelativeTime(syncHealth.last_webhook_synced_at ?? syncHealth.last_webhook_received_at)}</p>
                <p className="text-xs text-muted-foreground">Last Webhook Sync</p>
                {syncHealth.last_webhook_received_at && syncHealth.last_webhook_synced_at && syncHealth.last_webhook_received_at !== syncHealth.last_webhook_synced_at && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">Received {formatRelativeTime(syncHealth.last_webhook_received_at)}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// --- StepRow: reusable step rendering for Scenarios, By Tester, By Domain tabs ---

interface StepWithContext extends ReportStep {
  scenarioId: string;
  scenarioName: string;
  domain: string;
  area: string;
}

interface StepRowProps {
  step: ReportStep;
  isExpanded: boolean;
  onToggle: () => void;
  showTestPerson?: boolean;
  onViewIssue?: (issue: IssueSnapshot) => void;
}

const stepHasDetails = (step: ReportStep) =>
  !!(step.transaction || step.key_fields || step.comments || step.excel_status || step.issue_nr);

const StepRow = ({ step, isExpanded, onToggle, showTestPerson = true, onViewIssue }: StepRowProps) => {
  const ns = normalizeStatus(step.status);
  const hasDetails = stepHasDetails(step);


  const stepBorderColor =
    ns === 'closed' ? "bg-success/5 border-l-success"
    : ns === 'ready_to_validate' ? "bg-review/5 border-l-review"
    : ns === 'ongoing' ? "bg-info/5 border-l-info"
    : ns === 'retest' ? "bg-warning/5 border-l-warning"
    : ns === 'errors' ? "bg-destructive/5 border-l-destructive"
    : "bg-muted/50 border-l-muted-foreground";

  const stepNumColor =
    ns === 'closed' ? "bg-success/20 text-success"
    : ns === 'ready_to_validate' ? "bg-review/20 text-review"
    : ns === 'ongoing' ? "bg-info/20 text-info"
    : ns === 'retest' ? "bg-warning/20 text-warning"
    : ns === 'errors' ? "bg-destructive/20 text-destructive"
    : "bg-muted text-muted-foreground";

  return (
    <div>
      <button
        type="button"
        onClick={() => hasDetails && onToggle()}
        className={cn(
          "w-full flex items-center gap-3 p-3 rounded-lg border-l-4 text-left",
          hasDetails && "cursor-pointer hover:bg-accent/5",
          !hasDetails && "cursor-default",
          stepBorderColor
        )}
      >
        <div className={cn(
          "w-12 h-8 rounded flex items-center justify-center text-sm font-mono font-bold shrink-0",
          stepNumColor
        )}>
          {step.step_number}
        </div>
        <span className="flex-1 text-sm min-w-0 truncate">{step.description}</span>
        {step.transaction && (
          <Badge variant="outline" className="text-xs shrink-0 font-mono">
            {step.transaction}
          </Badge>
        )}

        {step.has_logged_issue && step.issue && onViewIssue && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewIssue(step.issue!); }}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-700 border border-red-200 hover:bg-red-200 transition-colors cursor-pointer shrink-0"
          >
            <Bug className="h-3 w-3" />
            Issue Logged
          </button>
        )}
        {step.due_date && (
          <span className={cn(
            "text-xs flex items-center gap-1 shrink-0",
            isDueDateOverdue(step.due_date) && !DONE_STATUSES.has(ns as TestCaseStatus)
              ? "text-destructive font-medium"
              : "text-muted-foreground"
          )}>
            <Calendar className="h-3 w-3" />
            {formatDueDate(step.due_date)}
            {isDueDateOverdue(step.due_date) && !DONE_STATUSES.has(ns as TestCaseStatus) && (
              <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">
                Overdue
              </Badge>
            )}
          </span>
        )}
        {showTestPerson && step.test_person && (
          <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
            <User className="h-3 w-3" />
            {step.test_person}
          </span>
        )}
        <StatusBadge status={step.status} variant="compact" />
        {hasDetails && (
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
            !isExpanded && "-rotate-90"
          )} />
        )}
      </button>
      {isExpanded && hasDetails && (
        <div className="ml-16 mr-2 mt-1 mb-2 p-3 rounded-lg bg-muted/30 border border-border space-y-2 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {step.key_fields && (
              <div>
                <span className="text-muted-foreground font-medium">Key Fields</span>
                <p className="mt-0.5">{step.key_fields}</p>
              </div>
            )}
            {step.excel_status && (
              <div>
                <span className="text-muted-foreground font-medium">Excel Status</span>
                <p className="mt-0.5">{step.excel_status}</p>
              </div>
            )}
          </div>
          {step.issue_nr && (
            <div>
              <span className="text-muted-foreground font-medium">Issue Nr</span>
              <p className="mt-0.5 font-mono text-xs">{step.issue_nr}</p>
            </div>
          )}
          {step.comments && (
            <div>
              <span className="text-muted-foreground font-medium">Comments</span>
              <p className="mt-0.5 whitespace-pre-line">{step.comments}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const groupByScenario = (steps: StepWithContext[]): Record<string, { scenarioName: string; area: string; steps: StepWithContext[] }> => {
  const groups: Record<string, { scenarioName: string; area: string; steps: StepWithContext[] }> = {};
  for (const step of steps) {
    if (!groups[step.scenarioId]) {
      groups[step.scenarioId] = { scenarioName: step.scenarioName, area: step.area, steps: [] };
    }
    groups[step.scenarioId].steps.push(step);
  }
  return groups;
};

const ProjectReport = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'scenarios' | 'testers' | 'domains' | 'areas' | 'sync'>('scenarios');
  const [expandedScenarios, setExpandedScenarios] = useState<string[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [lazySteps, setLazySteps] = useState<Record<string, ReportStep[]>>({});
  const [loadingSteps, setLoadingSteps] = useState<Set<string>>(new Set());
  const [scenariosPage, setScenariosPage] = useState(1);
  const [scenariosPageSize, setScenariosPageSize] = useState(20);
  const [sourceFilesPage, setSourceFilesPage] = useState(1);
  const [sourceFilesOpen, setSourceFilesOpen] = useState(false);
  const [expandedTesters, setExpandedTesters] = useState<string[]>([]);
  const [viewIssue, setViewIssue] = useState<IssueSnapshot | null>(null);

  // Filter state — manages UI state and converts to API query params.
  // Report is passed for filter_options (dropdowns); filtering itself is server-side.
  const [reportData, setReportData] = useState<ProjectReportResponse | undefined>(undefined);
  const reportFilters = useReportFilters(reportData);
  const { isExporting, startExport } = useReportExport();
  const { filterParams, isFiltered } = reportFilters;

  // Server-side filtered + paginated report
  const reportQueryParams = useMemo(() => ({
    ...filterParams,
    page: scenariosPage,
    page_size: scenariosPageSize,
  }), [filterParams, scenariosPage, scenariosPageSize]);

  const { data: report, isLoading, isFetching, error, refetch, dataUpdatedAt } = useProjectReport(id!, reportQueryParams, {
    enabled: !!id,
    refetchInterval: 60000,
  });
  // `isLoading` is true only on the cold initial load; `placeholderData`
  // keeps the previous report visible across filter changes, so we surface
  // an inline indicator from `isFetching` to confirm a refetch is running.
  const isRefetching = isFetching && !isLoading;

  // Update the cached report data for filter options when new data arrives
  useEffect(() => {
    if (report) setReportData(report);
  }, [report]);

  const recreateSubscriptions = useRecreateSubscriptions(id!);

  // Count steps with issues for the filter toggle badge
  const issueCount = useMemo(() => {
    if (!report?.scenarios) return 0;
    return report.scenarios.reduce(
      (acc, s) => acc + s.steps.filter((st) => !!st.has_logged_issue).length,
      0,
    );
  }, [report?.scenarios]);

  // Reset scenarios page when filters change
  useEffect(() => {
    setScenariosPage(1);
  }, [filterParams]);

  // Flatten steps with scenario context for "By Tester" drill-down
  // Uses only the current page's scenarios (bounded by page_size)
  const allStepsWithContext: StepWithContext[] = useMemo(() =>
    (report?.scenarios ?? []).flatMap(scenario =>
      scenario.steps.map(step => ({
        ...step,
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        domain: step.test_person || scenario.domain,
        area: step.area || scenario.area,
      }))
    ),
    [report?.scenarios]
  );

  // Steps keyed by test_person for "By Tester" drill-down
  const stepsByTester = useMemo(() => {
    const map: Record<string, StepWithContext[]> = {};
    for (const step of allStepsWithContext) {
      const key = step.test_person || '(Unassigned)';
      if (!map[key]) map[key] = [];
      map[key].push(step);
    }
    return map;
  }, [allStepsWithContext]);
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="Project Report" showBack backPath="/admin/projects" />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading report...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !report?.project) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="Project Report" showBack backPath="/admin/projects" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">
              {error ? "Failed to Load Report" : "Project Not Found"}
            </h2>
            <p className="text-muted-foreground">
              {error instanceof Error
                ? error.message
                : "The project you're looking for doesn't exist."}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const project = report.project;
  const stats = report.overall_stats;
  const testerBreakdown = report.tester_breakdown;
  const scenarios = report.scenarios;

  const toggleScenario = (scenarioId: string) => {
    const isExpanding = !expandedScenarios.includes(scenarioId);
    setExpandedScenarios(prev =>
      prev.includes(scenarioId)
        ? prev.filter(id => id !== scenarioId)
        : [...prev, scenarioId]
    );

    // Lazy-load steps if scenario has none (large project mode)
    if (isExpanding && !lazySteps[scenarioId]) {
      const scenario = report?.scenarios?.find(s => s.id === scenarioId);
      if (scenario && (!scenario.steps || scenario.steps.length === 0) && scenario.total_steps > 0) {
        // scenarios_get returns raw STEP entities without issue context;
        // overlay the snapshot map computed by the lightweight report path
        // so "Issue Logged" still renders for issue-bearing steps.
        const issueSnapshots = scenario.issue_snapshots ?? {};
        setLoadingSteps(prev => new Set(prev).add(scenarioId));
        scenariosApi.get(scenarioId).then(res => {
          const steps: ReportStep[] = (res.steps || []).map((s: Record<string, string>, i: number) => {
            const stepNumber = Number(s.step_number || s.number || i + 1);
            const snapshot = issueSnapshots[String(stepNumber)];
            return {
              step_number: stepNumber,
              description: s.description || s.Action || '',
              expected_result: s.expected_result || '',
              status: (s.status || 'new') as ReportStep['status'],
              test_person: s.test_person || s.Tester || '',
              area: s.area || s.Area || '',
              due_date: s.due_date || undefined,
              updated_at: s.updated_at || '',
              transaction: s.transaction || undefined,
              key_fields: s.key_fields || undefined,
              comments: s.comments || undefined,
              excel_status: s.excel_status || undefined,
              issue_nr: s.issue_nr || undefined,
              ...(snapshot ? { has_logged_issue: true, issue: snapshot } : {}),
            };
          });
          setLazySteps(prev => ({ ...prev, [scenarioId]: steps }));
          setLoadingSteps(prev => { const n = new Set(prev); n.delete(scenarioId); return n; });
        }).catch(() => {
          setLoadingSteps(prev => { const n = new Set(prev); n.delete(scenarioId); return n; });
          toast.error('Failed to load steps');
        });
      }
    }
  };

  const toggleStep = (stepKey: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepKey)) next.delete(stepKey);
      else next.add(stepKey);
      return next;
    });
  };

  const percentage = stats.progress_percentage;

  // Timeline calculations — prefer start_date, fall back to created_at
  const hasStartDate = !!project.start_date;
  const startDate = (project.start_date || project.created_at) ? new Date(project.start_date || project.created_at) : new Date();
  const deadlineDate = project.end_date ? new Date(project.end_date) : new Date();
  const today = new Date();
  const totalDuration = deadlineDate.getTime() - startDate.getTime();
  const elapsed = today.getTime() - startDate.getTime();
  const timelineProgress = totalDuration > 0
    ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100))
    : 0;
  const daysLeft = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysLeft < 0;
  const totalDays = totalDuration > 0
    ? Math.ceil(totalDuration / (1000 * 60 * 60 * 24))
    : 0;

  // Use filtered stats for accurate counts
  const totalDone = (stats.ready_to_validate ?? 0) + (stats.closed ?? 0);
  const totalActive = (stats.ongoing ?? 0) + (stats.retest ?? 0);
  const totalProblems = (stats.errors ?? 0);
  // Stats are now server-side filtered — total_steps reflects the active filters

  // Domain/area breakdowns from server (already filtered server-side)
  const domainBreakdown = report.domain_breakdown;
  const areaBreakdown = report.area_breakdown;
  // Distinct test persons drive the "By Script / Domain" tab — the tab renders
  // one row per unique tester, so the count must mirror that rather than the
  // raw breakdown length (which is one row per (tester × domain) combo).
  const distinctTestersCount = new Set(
    testerBreakdown.map((t) => t.test_person).filter(Boolean),
  ).size;

  const sourceFiles: SourceFile[] = report.source_files ?? [];

  // SharePoint section visibility: show whenever the project is SharePoint-linked
  // (folder set, scenario has drive/item ids) OR when subscriptions already exist
  // for this project. The latter prevents the section from being hidden when a
  // project was created via Excel upload but later had subscriptions attached
  // (or scenarios lost their sharepoint_drive_id/item_id during a sync).
  const hasSharePointFiles =
    !!project.sharepoint_folder
    || sourceFiles.some(f => f.item_id && f.drive_id)
    || (report.sync_health?.total_subscriptions ?? 0) > 0;

  // Group tester entries by domain for the domain tab drill-down
  const testersByDomain = testerBreakdown.reduce((acc, t) => {
    if (!acc[t.domain]) acc[t.domain] = [];
    acc[t.domain].push(t);
    return acc;
  }, {} as Record<string, TesterBreakdownEntry[]>);

  const toggleTester = (person: string) => {
    setExpandedTesters(prev =>
      prev.includes(person)
        ? prev.filter(p => p !== person)
        : [...prev, person]
    );
  };

  const getProgressColor = (pct: number) => {
    if (pct === 0) return 'bg-muted';
    if (pct >= 75) return 'bg-success';
    if (pct >= 50) return 'bg-info';
    if (pct >= 25) return 'bg-warning';
    return 'bg-destructive';
  };


  const getScenarioProgress = (scenario: typeof scenarios[0]) => {
    if (scenario.total_steps === 0) return 0;
    return Math.round((scenario.completed_steps / scenario.total_steps) * 100);
  };

  const projectStatus = projectStatusConfig[project.status] ?? projectStatusConfig['active'];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        title={`${project.project_number} - ${project.name}`}
        showBack
        backPath="/admin/projects"
      />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Project Header */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                  {project.project_number} - {project.name}
                </h1>
                <Badge variant="outline" className={projectStatus.color}>
                  {projectStatus.label}
                </Badge>
              </div>
              <p className="text-muted-foreground">{project.description}</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              {project.created_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {hasStartDate ? "Started" : "Created"} {startDate.toLocaleDateString()}
                </div>
              )}
              {project.end_date && (
                <div className={cn(
                  "flex items-center gap-1 font-medium",
                  isOverdue ? "text-destructive" : daysLeft <= 7 ? "text-warning" : "text-success"
                )}>
                  <Clock className="h-4 w-4" />
                  {isOverdue ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-xs">
                  {dataUpdatedAt ? formatRelativeTime(new Date(dataUpdatedAt).toISOString()) : '—'}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => refetch()}
                  title="Refresh report data"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Timeline Gauge */}
          {project.end_date && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Project Timeline
                </CardTitle>
                <CardDescription>
                  {totalDays} day project • {hasStartDate ? "Started" : "Created"} {startDate.toLocaleDateString()} • Deadline {deadlineDate.toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Timeline bar */}
                  <div className="relative">
                    <div className="h-8 rounded-full bg-muted overflow-hidden relative" role="progressbar" aria-valuenow={Math.round(timelineProgress)} aria-valuemin={0} aria-valuemax={100} aria-label="Project timeline progress">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          isOverdue ? "bg-destructive" : timelineProgress > 75 ? "bg-warning" : "bg-primary"
                        )}
                        style={{ width: `${timelineProgress}%` }}
                      />
                      <div
                        className="absolute top-0 h-full w-1 bg-foreground"
                        style={{ left: `${Math.min(timelineProgress, 100)}%`, transform: 'translateX(-50%)' }}
                      >
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap bg-foreground text-background px-2 py-0.5 rounded">
                          Today
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                      <span>{startDate.toLocaleDateString()}</span>
                      <span className={cn(
                        "font-medium",
                        isOverdue ? "text-destructive" : ""
                      )}>
                        {deadlineDate.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Time elapsed: </span>
                      <span className="font-medium">{Math.round(timelineProgress)}%</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Work completed: </span>
                      <span className={cn(
                        "font-medium",
                        percentage >= timelineProgress ? "text-success" : "text-warning"
                      )}>{Math.round(percentage)}%</span>
                    </div>
                    <div className={cn(
                      "text-sm font-semibold px-3 py-1 rounded-full",
                      isOverdue
                        ? "bg-destructive/10 text-destructive"
                        : daysLeft <= 7
                          ? "bg-warning/10 text-warning"
                          : "bg-success/10 text-success"
                    )}>
                      {isOverdue ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days remaining`}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalDone}<span className="text-base font-normal text-muted-foreground">/{stats.total_steps}</span></p>
                    <p className="text-sm text-muted-foreground">
                      Done
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-info/10 flex items-center justify-center">
                    <Timer className="h-6 w-6 text-info" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalActive}<span className="text-base font-normal text-muted-foreground">/{stats.total_steps}</span></p>
                    <p className="text-sm text-muted-foreground">
                      Active
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{totalProblems}<span className="text-base font-normal text-muted-foreground">/{stats.total_steps}</span></p>
                    <p className="text-sm text-muted-foreground">
                      Errors
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{Math.round(percentage)}%</p>
                    <p className="text-sm text-muted-foreground">Overall</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Source Files Summary */}
          {sourceFiles.length > 0 && (
            <Collapsible open={sourceFilesOpen} onOpenChange={setSourceFilesOpen}>
            <Card>
              <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {sourceFilesOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  Source Files
                </CardTitle>
                <CardDescription className="ml-7">
                  {sourceFiles.length} Excel file{sourceFiles.length !== 1 ? 's' : ''} imported
                </CardDescription>
              </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
              <CardContent>
                {(() => {
                  const totalPages = Math.ceil(sourceFiles.length / SOURCE_FILES_PAGE_SIZE);
                  const safePage = Math.min(sourceFilesPage, totalPages || 1);
                  const startIdx = (safePage - 1) * SOURCE_FILES_PAGE_SIZE;
                  const pageFiles = sourceFiles.slice(startIdx, startIdx + SOURCE_FILES_PAGE_SIZE);

                  return (
                    <>
                      <div className="space-y-2">
                        {pageFiles.map((file) => (
                          <div
                            key={file.item_id || file.file_name}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileSpreadsheet className="h-4 w-4 text-success shrink-0" />
                              <span className="font-medium text-sm truncate">{file.file_name}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              <Badge variant="secondary">
                                {file.scenario_count} scenario{file.scenario_count !== 1 ? 's' : ''}
                              </Badge>
                              {file.web_url && (
                                <Button variant="outline" size="sm" asChild
                                  className="h-7 gap-1.5 px-2.5 text-xs font-medium text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50 shrink-0">
                                  <a href={file.web_url} target="_blank" rel="noopener noreferrer">
                                    <FileSpreadsheet className="h-3.5 w-3.5" />
                                    Open
                                    <ExternalLink className="h-3 w-3 opacity-50" />
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {totalPages > 1 && (
                        <Pagination className="mt-4">
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious
                                onClick={() => setSourceFilesPage(p => Math.max(1, p - 1))}
                                className={cn(safePage <= 1 && "pointer-events-none opacity-50")}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                              .map((p, idx, arr) => (
                                <Fragment key={p}>
                                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                                    <PaginationItem><PaginationEllipsis /></PaginationItem>
                                  )}
                                  <PaginationItem>
                                    <PaginationLink
                                      isActive={p === safePage}
                                      onClick={() => setSourceFilesPage(p)}
                                    >
                                      {p}
                                    </PaginationLink>
                                  </PaginationItem>
                                </Fragment>
                              ))}
                            <PaginationItem>
                              <PaginationNext
                                onClick={() => setSourceFilesPage(p => Math.min(totalPages, p + 1))}
                                className={cn(safePage >= totalPages && "pointer-events-none opacity-50")}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      )}
                    </>
                  );
                })()}
              </CardContent>
              </CollapsibleContent>
            </Card>
            </Collapsible>
          )}

          {/* Refetch progress bar — aggressive, unmissable. Sits above the
              filter panel so users immediately know their filter triggered
              a server-side refetch. Uses an indeterminate animation rather
              than a percentage because the report fetch has no progress
              event stream. */}
          {isRefetching && (
            <div
              className="sticky top-0 z-40 -mx-4 sm:-mx-6 lg:-mx-8 h-1 overflow-hidden bg-primary/15"
              role="progressbar"
              aria-busy="true"
              aria-label="Updating report"
            >
              <div className="h-full w-full origin-left bg-primary animate-indeterminate-progress" />
            </div>
          )}

          {/* Filter Panel (always visible) */}
          <div className="relative">
            <ReportFilterPanel
              hook={reportFilters}
              issueCount={issueCount}
            />
            {isRefetching && (
              <div
                className="absolute right-3 top-3 flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary shadow-sm"
                role="status"
                aria-live="polite"
              >
                <LoadingSpinner size="sm" className="text-primary" />
                <span>Updating filtered results…</span>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="scenarios">
                  Scenarios{" "}
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    ({report.pagination ? report.pagination.total_scenarios : scenarios.length}
                    {isFiltered ? " filtered" : ""})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="testers">
                  By Script / Domain{" "}
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    ({distinctTestersCount})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="domains">
                  By Domain{" "}
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    ({domainBreakdown.length})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="areas">
                  By Area{" "}
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    ({areaBreakdown.length})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="sync" className="gap-2">
                  <Activity className="h-3 w-3" />
                  Activity
                </TabsTrigger>
              </TabsList>
              <ReportFilterBar
                hook={reportFilters}
                isExporting={isExporting}
                exportsHistoryPath={id ? `/admin/projects/${id}/exports` : undefined}
                onExport={() => {
                  const f = reportFilters.filters;
                  const activeFilters = {
                    ...(f.domain.length > 0 && { domain: f.domain }),
                    ...(f.area.length > 0 && { area: f.area }),
                    ...(f.status.length > 0 && { status: f.status }),
                    ...(f.tester.length > 0 && { tester: f.tester }),
                  };
                  startExport(id!, {
                    filters: Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
                    projectNumber: project.project_number,
                  });
                }}
                onExportAll={isFiltered ? () => {
                  startExport(id!, { projectNumber: project.project_number });
                } : undefined}
              />
            </div>

            {/* Scenarios Tab */}
            <TabsContent value="scenarios" className="mt-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    Test Scenarios
                  </CardTitle>
                  <CardDescription>
                    Click on a scenario to view individual test steps
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {scenarios.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">
                        {isFiltered ? 'No scenarios match the current filters' : 'No scenarios found'}
                      </p>
                      {isFiltered && (
                        <Button variant="link" size="sm" onClick={reportFilters.resetFilters} className="mt-2">
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  ) : (
                    (() => {
                      const totalPages = report.pagination?.total_pages ?? Math.ceil(scenarios.length / scenariosPageSize);
                      const safePage = report.pagination?.page ?? Math.min(scenariosPage, totalPages || 1);
                      // Scenarios are already paginated server-side
                      const pageScenarios = scenarios;

                      return (
                        <>
                          {pageScenarios.map((scenario) => {
                            const scenarioPct = getScenarioProgress(scenario);
                            const isComplete = scenarioPct === 100;
                            const isExpanded = expandedScenarios.includes(scenario.id);

                            return (
                              <Collapsible
                                key={scenario.id}
                                open={isExpanded}
                                onOpenChange={() => toggleScenario(scenario.id)}
                              >
                                <CollapsibleTrigger className="w-full">
                                  <div className={cn(
                                    "flex items-center gap-4 p-4 rounded-lg border transition-all",
                                    isComplete
                                      ? "bg-success/5 border-success/20"
                                      : "bg-card hover:bg-accent/5",
                                    isExpanded && "ring-2 ring-primary/20"
                                  )}>
                                    {isExpanded ? (
                                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                                    ) : (
                                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                                    )}
                                    <div className="flex-1 text-left">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium">{scenario.name}</span>
                                        {scenario.area && scenario.area !== 'N/A' && (
                                          <Badge variant="outline">{scenario.area}</Badge>
                                        )}
                                        {scenario.source_file?.web_url && (
                                          <Button variant="outline" size="sm" asChild
                                            className="h-7 gap-1.5 px-2.5 text-xs font-medium text-primary border-primary/30 hover:bg-primary/10 hover:border-primary/50 shrink-0">
                                            <a href={scenario.source_file.web_url} target="_blank" rel="noopener noreferrer"
                                               onClick={(e) => e.stopPropagation()}>
                                              <FileSpreadsheet className="h-3.5 w-3.5" />
                                              Open
                                              <ExternalLink className="h-3 w-3 opacity-50" />
                                            </a>
                                          </Button>
                                        )}
                                        {isComplete && (
                                          <Badge className="bg-success text-success-foreground">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            Complete
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground">{scenario.description}</p>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                      <div className="text-right">
                                        <p className="text-sm font-medium">{scenarioPct}%</p>
                                        <p className="text-xs text-muted-foreground">
                                          {scenario.completed_steps}/{scenario.total_steps} steps
                                        </p>
                                      </div>
                                      <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                                        <div
                                          className={cn("h-full rounded-full", getProgressColor(scenarioPct))}
                                          style={{ width: `${scenarioPct}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="ml-9 mt-2 space-y-1 pb-2">
                                    {loadingSteps.has(scenario.id) && (
                                      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                        Loading steps...
                                      </div>
                                    )}
                                    {(scenario.steps.length > 0 ? scenario.steps : lazySteps[scenario.id] || []).map((step) => {
                                      const stepKey = `${scenario.id}-${step.step_number}`;
                                      return (
                                        <StepRow
                                          key={step.step_number}
                                          step={step}
                                          isExpanded={expandedSteps.has(stepKey)}
                                          onToggle={() => toggleStep(stepKey)}
                                          onViewIssue={setViewIssue}
                                        />
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            );
                          })}
                          <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>Show</span>
                              <Select
                                value={String(scenariosPageSize)}
                                onValueChange={(v) => { setScenariosPageSize(Number(v)); setScenariosPage(1); }}
                              >
                                <SelectTrigger className="w-[70px] h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAGE_SIZE_OPTIONS.map(size => (
                                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span>per page</span>
                            </div>
                            {totalPages > 1 && (
                              <Pagination className="mx-0 w-auto">
                                <PaginationContent>
                                  <PaginationItem>
                                    <PaginationPrevious
                                      onClick={() => setScenariosPage(p => Math.max(1, p - 1))}
                                      className={cn(safePage <= 1 && "pointer-events-none opacity-50")}
                                    />
                                  </PaginationItem>
                                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                                    .map((p, idx, arr) => (
                                      <Fragment key={p}>
                                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                                          <PaginationItem><PaginationEllipsis /></PaginationItem>
                                        )}
                                        <PaginationItem>
                                          <PaginationLink
                                            isActive={p === safePage}
                                            onClick={() => setScenariosPage(p)}
                                          >
                                            {p}
                                          </PaginationLink>
                                        </PaginationItem>
                                      </Fragment>
                                    ))}
                                  <PaginationItem>
                                    <PaginationNext
                                      onClick={() => setScenariosPage(p => Math.min(totalPages, p + 1))}
                                      className={cn(safePage >= totalPages && "pointer-events-none opacity-50")}
                                    />
                                  </PaginationItem>
                                </PaginationContent>
                              </Pagination>
                            )}
                          </div>
                        </>
                      );
                    })()
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Overview */}
            <TabsContent value="overview" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    Test Execution Details
                  </CardTitle>
                  <CardDescription>
                    Complete overview of all test assignments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {testerBreakdown.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No test assignments yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Domain</TableHead>
                          <TableHead>Area</TableHead>
                          <TableHead className="text-center">Total</TableHead>
                          <TableHead className="text-center">Closed</TableHead>
                          <TableHead className="text-center">Ongoing</TableHead>
                          <TableHead className="text-center">Errors</TableHead>
                          <TableHead className="w-[150px]">Progress</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {testerBreakdown.map((row, idx) => {
                          const rowDone = (row.ready_to_validate ?? 0) + (row.closed ?? 0);
                          const rowPct = row.total > 0 ? Math.round((rowDone / row.total) * 100) : 0;
                          return (
                            <TableRow key={`${row.test_person}-${row.domain}-${row.area}-${idx}`}>
                              <TableCell>{row.domain}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{row.area}</Badge>
                              </TableCell>
                              <TableCell className="text-center">{row.total}</TableCell>
                              <TableCell className="text-center text-success">{row.closed ?? 0}</TableCell>
                              <TableCell className="text-center text-info">{row.ongoing ?? 0}</TableCell>
                              <TableCell className="text-center text-destructive">{row.errors ?? 0}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                    <div
                                      className={cn("h-full rounded-full", getProgressColor(rowPct))}
                                      style={{ width: `${rowPct}%` }}
                                    />
                                  </div>
                                  <span className="text-xs w-8">{rowPct}%</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Testers */}
            <TabsContent value="testers" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Progress By Script / Domain
                  </CardTitle>
                  <CardDescription>
                    Click on a tester to view their individual test steps
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {testerBreakdown.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No test assignments yet</p>
                  ) : (
                    [...new Set(testerBreakdown.map(t => t.test_person).filter(Boolean))].map(person => {
                      const personData = testerBreakdown.filter(t => t.test_person === person);
                      const total = personData.reduce((a, b) => a + b.total, 0);
                      const done = personData.reduce((a, b) => a + (b.ready_to_validate ?? 0) + (b.closed ?? 0), 0);
                      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                      const isExpanded = expandedTesters.includes(person);
                      const personSteps = stepsByTester[person] ?? [];
                      const scenarioGroups = groupByScenario(personSteps);
                      const uniqueDomains = [...new Set(personData.map(d => d.domain))];

                      return (
                        <Collapsible
                          key={person}
                          open={isExpanded}
                          onOpenChange={() => toggleTester(person)}
                        >
                          <CollapsibleTrigger className="w-full">
                            <div className={cn(
                              "flex items-center gap-4 p-4 rounded-lg border transition-all",
                              pct === 100
                                ? "bg-success/5 border-success/20"
                                : "bg-card hover:bg-accent/5",
                              isExpanded && "ring-2 ring-primary/20"
                            )}>
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                              )}
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                                {(person ?? '').split(' ').map(n => n?.[0] ?? '').join('')}
                              </div>
                              <div className="flex-1 text-left">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold">{person}</span>
                                  {pct === 100 && (
                                    <Badge className="bg-success text-success-foreground">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      Complete
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {(() => {
                                    const sc = personData.reduce((a, b) => a + (b.scenario_count ?? 0), 0) || Object.keys(scenarioGroups).length;
                                    return `${sc} scenario${sc !== 1 ? 's' : ''}`;
                                  })()} across {uniqueDomains.length} domain{uniqueDomains.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-4 shrink-0">
                                <div className="text-right">
                                  <p className="text-sm font-medium">{pct}%</p>
                                  <p className="text-xs text-muted-foreground">
                                    {done}/{total} steps
                                  </p>
                                </div>
                                <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className={cn("h-full rounded-full", getProgressColor(pct))}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="ml-9 mt-2 space-y-4 pb-2">
                              {Object.entries(scenarioGroups).map(([scenarioId, group]) => (
                                <div key={scenarioId}>
                                  <div className="flex items-center gap-2 mb-2 px-1">
                                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">{group.scenarioName}</span>
                                    <Badge variant="outline" className="text-xs">{group.area}</Badge>
                                  </div>
                                  <div className="space-y-1">
                                    {group.steps.map(step => {
                                      const stepKey = `${scenarioId}-${step.step_number}`;
                                      return (
                                        <StepRow
                                          key={stepKey}
                                          step={step}
                                          isExpanded={expandedSteps.has(stepKey)}
                                          onToggle={() => toggleStep(stepKey)}
                                          showTestPerson={false}
                                          onViewIssue={setViewIssue}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* By Domain Tab */}
            <TabsContent value="domains" className="mt-6">
              {domainBreakdown.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground text-center py-8">No domain data available</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {report.has_compound_domains && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Some scenarios span multiple domains — their step counts contribute to each
                      domain card, so per-domain totals can sum to more than the project total.
                    </p>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                  {domainBreakdown.map(domainData => {
                    const pct = Math.round(domainData.progress_percentage);
                    const domainTesters = testersByDomain[domainData.domain] ?? [];
                    const uniqueTesters = [...new Set(domainTesters.map(t => t.test_person).filter(Boolean))];
                    return (
                      <Card key={domainData.domain}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">{domainData.domain}</CardTitle>
                              <p className="text-xs text-muted-foreground mt-0.5">{domainData.total_steps} steps</p>
                            </div>
                            <Badge variant="outline">{pct}%</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="h-3 rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${domainData.domain} progress`}>
                            <div
                              className={cn("h-full rounded-full", getProgressColor(pct))}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center text-sm">
                            <div className="p-2 rounded bg-success/10">
                              <p className="font-semibold text-success">{(domainData.ready_to_validate ?? 0) + (domainData.closed ?? 0)}</p>
                              <p className="text-xs text-muted-foreground">Done</p>
                            </div>
                            <div className="p-2 rounded bg-info/10">
                              <p className="font-semibold text-info">{domainData.ongoing ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Ongoing</p>
                            </div>
                            <div className="p-2 rounded bg-destructive/10">
                              <p className="font-semibold text-destructive">{domainData.errors ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Errors</p>
                            </div>
                          </div>
                          {uniqueTesters.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Testers: {uniqueTesters.join(", ")}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="areas" className="mt-6">
              {areaBreakdown.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground text-center py-8">No area data available</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {areaBreakdown.map(area => {
                    const pct = Math.round(area.progress_percentage);
                    const areaTesters = testerBreakdown.filter(t => t.area === area.area);
                    const uniqueTesters = [...new Set(areaTesters.map(t => t.test_person).filter(Boolean))];
                    return (
                      <Card key={area.area}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">Area {area.area}</CardTitle>
                              <p className="text-xs text-muted-foreground mt-0.5">{area.total_steps} steps</p>
                            </div>
                            <Badge variant="outline">{pct}%</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="h-3 rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Area ${area.area} progress`}>
                            <div
                              className={cn("h-full rounded-full", getProgressColor(pct))}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center text-sm">
                            <div className="p-2 rounded bg-success/10">
                              <p className="font-semibold text-success">{(area.ready_to_validate ?? 0) + (area.closed ?? 0)}</p>
                              <p className="text-xs text-muted-foreground">Done</p>
                            </div>
                            <div className="p-2 rounded bg-info/10">
                              <p className="font-semibold text-info">{area.ongoing ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Ongoing</p>
                            </div>
                            <div className="p-2 rounded bg-destructive/10">
                              <p className="font-semibold text-destructive">{area.errors ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Errors</p>
                            </div>
                          </div>
                          {uniqueTesters.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Testers: {uniqueTesters.join(", ")}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="sync" className="mt-6 space-y-6">
              {/* SharePoint Sync Section — only for projects with SharePoint files */}
              {hasSharePointFiles ? (
                <>
                  {report.sync_health && (
                    <SyncHealthCard syncHealth={report.sync_health} />
                  )}

                  {report.sync_health && (() => {
                    const hasNoSubs = report.sync_health.total_subscriptions === 0;
                    const hasIssues = report.sync_health.expired_subscriptions > 0 || report.sync_health.circuit_broken_count > 0;

                    const issueDescription = hasNoSubs
                      ? 'This project has SharePoint-linked scenarios but no webhook subscriptions. Recreate them to enable real-time sync notifications from Graph API.'
                      : hasIssues
                        ? [
                            report.sync_health.expired_subscriptions > 0 && `${report.sync_health.expired_subscriptions} expired`,
                            report.sync_health.circuit_broken_count > 0 && `${report.sync_health.circuit_broken_count} circuit-broken`,
                          ].filter(Boolean).join(' and ') +
                          ` subscription${(report.sync_health.expired_subscriptions + report.sync_health.circuit_broken_count) !== 1 ? 's' : ''}. Recreating will delete all existing subscriptions and create fresh ones.`
                        : 'Subscriptions are healthy. Use this to recreate them if webhooks stop working.';

                    return (
                      <Card className={cn(hasNoSubs && "border-warning/50")}>
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-lg">
                            {hasNoSubs ? (
                              <WifiOff className="h-5 w-5 text-warning" />
                            ) : hasIssues ? (
                              <ShieldAlert className="h-5 w-5 text-warning" />
                            ) : (
                              <Wifi className="h-5 w-5 text-green-600" />
                            )}
                            {hasNoSubs ? 'No Active Subscriptions' : hasIssues ? 'Subscription Issues Detected' : 'Webhook Subscriptions'}
                          </CardTitle>
                          <CardDescription>{issueDescription}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Button
                            variant={hasNoSubs ? "default" : "outline"}
                            onClick={() => {
                              const promise = recreateSubscriptions.mutateAsync();
                              toast.promise(promise, {
                                loading: 'Recreating subscriptions...',
                                success: (result) =>
                                  `Created ${result.created} subscription${result.created !== 1 ? 's' : ''}` +
                                  (result.failed > 0 ? ` (${result.failed} failed)` : '') +
                                  (result.cleaned_up > 0 ? `, cleaned up ${result.cleaned_up} old` : ''),
                                error: (err) => `Failed: ${err.message}`,
                              });
                            }}
                            disabled={recreateSubscriptions.isPending}
                            className="gap-2"
                          >
                            <RefreshCw className={cn(
                              "h-4 w-4",
                              recreateSubscriptions.isPending && "animate-spin"
                            )} />
                            {recreateSubscriptions.isPending ? 'Recreating...' : 'Recreate Subscriptions'}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  <SyncActivityLog projectId={id!} />
                </>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <WifiOff className="h-5 w-5 text-muted-foreground" />
                      Sync Not Available
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      This project was created without SharePoint files. Real-time sync is not available.
                      To enable sync, create a new project by importing Excel files from SharePoint.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Activity Logs — always visible regardless of SharePoint status */}
              <MappingActivityLog projectId={id!} />
              <TestCaseActivityLog projectId={id!} />
              <IssueActivityLog projectId={id!} />
              <ProjectActivityLog projectId={id!} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <footer className="w-full py-4 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Lotus Bakeries. Internal Testing Portal.
          </p>
        </div>
      </footer>

      {viewIssue && (
        <IssueDetailDialog
          issue={viewIssue}
          open={!!viewIssue}
          onOpenChange={(open) => !open && setViewIssue(null)}
        />
      )}
    </div>
  );
};

export default ProjectReport;
